import {
  AdminDollarInput2,
  AdminInput,
  AdminSelect,
  Button,
  Div,
  EditPenIconRed,
  H5,
  Icon,
  Img,
  Modal,
  P,
  PlusIcon,
  PropsModal,
  TrashIconRed,
  displayCentsInDollars,
  lightTheme,
  renderProviderIcon,
  renderProviderName,
} from '@vgrubs/components';
import {
  Availability,
  CategoryV2Response,
  MenuItemV2Response,
  MenuPriceCustomizationV2Response,
  MenuV2Response,
} from 'components/src/types/menu.types';
import { Provider } from 'components/src/types/shared.types';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ImageInput } from '../../components/ImageInput';
import { AddCustomPrice } from './AddCustomPrice';
import { DeleteCustomPriceModal } from './DeleteCustomPriceModal';
import { DeleteImageModal } from './DeleteImageModal';
import { AddModifierGroupComponent } from './ModifierGroupComponents';

export const AddItemModal = ({
  addModal,
  closeModal,
  edit,
  setAddModifierGroupModal,
  menu,
  modifierGroups,
  setModifierGroups,
  handleAddItem,
  handleEditItem,
  provider,
  handleUploadImage,
  readOnly,
}: PropsModal & {
  setAddModifierGroupModal: Dispatch<SetStateAction<boolean>>;
  addModal:
    | boolean
    | { item: MenuItemV2Response; category: CategoryV2Response }
    | null;
  menu: MenuV2Response | null;
  handleAddItem?: (
    image: MenuItemV2Response['imageUrl'] | File | Blob | null,
    name: MenuItemV2Response['name'],
    description: MenuItemV2Response['description'],
    price: MenuItemV2Response['price'],
    categoryId: CategoryV2Response['id'],
    modifierGroups: MenuItemV2Response['modifierGroups'],
    customizedPrices: MenuItemV2Response['customizedPrices'],
    availability: Availability,
    preparationNotes: string,
    emptyForm: () => void
  ) => void;
  handleEditItem?: (
    image: MenuItemV2Response['imageUrl'] | File | Blob | null,
    name: MenuItemV2Response['name'],
    description: MenuItemV2Response['description'],
    price: MenuItemV2Response['price'],
    categoryId: CategoryV2Response['id'],
    modifierGroups: MenuItemV2Response['modifierGroups'],
    existingItem: MenuItemV2Response,
    existingCategoryId: CategoryV2Response['id'],
    customizedPrices: MenuItemV2Response['customizedPrices'],
    availability: Availability,
    preparationNotes: string,
    emptyForm: () => void
  ) => void;
  edit?: boolean;
  provider: Provider;
  modifierGroups: MenuItemV2Response['modifierGroups'];
  setModifierGroups: Dispatch<
    SetStateAction<MenuItemV2Response['modifierGroups']>
  >;
  handleUploadImage: (
    image: File | Blob | string | null
  ) => Promise<string | undefined>;
  readOnly?: boolean;
}) => {
  const [image, setImage] = useState<string | File | Blob | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [category, setCategory] = useState<CategoryV2Response['id']>(0);
  const [price, setPrice] = useState(0);

  const [addCustomPriceModal, setAddCustomPriceModal] = useState(false);
  const [editCustomPriceModal, setEditCustomPriceModal] =
    useState<MenuPriceCustomizationV2Response | null>(null);

  const [customizedPrices, setCustomizedPrices] = useState<
    MenuPriceCustomizationV2Response[]
  >([]);

  const [availability, setAvailability] = useState(Availability.Available);
  const [preparationNotes, setPreparationNotes] = useState('');
  const [pausedUntilEndOfTheDay, setPausedUntilEndOfTheDay] = useState(false);
  const [deleteModal, setDeleteModal] =
    useState<MenuPriceCustomizationV2Response | null>(null);
  const [deleteImageModal, setDeleteImageModal] = useState(false);

  const data = addModal;

  const handleValueTotalChange = useCallback((val: number) => {
    setPrice(val);
  }, []);

  const availabilityOptions = (
    <optgroup label="Select Availability">
      <option
        value={JSON.stringify({
          availability: Availability.Available,
          pausedUntilEndOfTheDay: false,
        })}
      >
        In Stock
      </option>
      <option
        value={JSON.stringify({
          availability: Availability.SoldOut,
          pausedUntilEndOfTheDay: false,
        })}
      >
        Out of Stock (Indefinitely)
      </option>
      {/* <option
        value={JSON.stringify({
          availability: Availability.SoldOut,
          pausedUntilEndOfTheDay: true,
        })}
      >
        Out of Stock (Till End of Day)
      </option> */}
    </optgroup>
  );

  const typeOptions = (
    <optgroup label="Select Category">
      {menu?.categories?.map((r, i) => (
        <option value={r.id} key={i}>
          {r.name}
        </option>
      ))}
    </optgroup>
  );

  useEffect(() => {
    setCategory(menu?.categories?.[0]?.id || 0);
    if (edit && typeof data === 'object') {
      setName(data?.item?.name || '');
      setDescription(data?.item?.description || '');
      setCategory(data?.category?.id || menu?.categories?.[0]?.id || 0);
      setPrice(data?.item?.price || 0);
      setModifierGroups(data?.item?.modifierGroups || []);
      setImage(data?.item?.imageUrl || '');
      setCustomizedPrices(data?.item?.customizedPrices || []);
      setPreparationNotes(data?.item?.preparationNotes || '');
    } else {
      emptyForm();
    }
  }, [data]);

  const emptyForm = () => {
    setImage('');
    setName('');
    setDescription('');
    setCategory(menu?.categories?.[0]?.id || 0);
    setModifierGroups([]);
    setPrice(0);
    setCustomizedPrices([]);
    setPreparationNotes('');
  };

  const handleSubmit = () => {
    if (edit && typeof data === 'object') {
      handleEditItem?.(
        image,
        name,
        description,
        price,
        category,
        modifierGroups,
        data?.item as MenuItemV2Response,
        data?.category?.id as CategoryV2Response['id'],
        customizedPrices,
        availability,
        preparationNotes,
        emptyForm
      );
    } else {
      handleAddItem?.(
        image,
        name,
        description,
        price,
        category,
        modifierGroups,
        customizedPrices,
        availability,
        preparationNotes,
        emptyForm
      );
    }
  };

  const handleImageChange = async (image: File | Blob | string | null) => {
    const imageUrl = await handleUploadImage(image);
    if (imageUrl) {
      setImage(imageUrl);
    }
  };

  return (
    <>
      <Modal
        isOpen={!!addModal}
        closeModal={closeModal}
        header={readOnly ? 'Item' : edit ? 'Edit Item' : 'Add Item'}
        logo={false}
        footer={
          readOnly ? undefined : (
            <Button $selected $w="100%" onClick={handleSubmit}>
              Confirm
            </Button>
          )
        }
        $numOfFloatingButtons={readOnly ? 0 : 1}
      >
        <Div $flex $center $mb="32">
          <ImageInput
            image={image}
            onChange={(img) => handleImageChange(img)}
            onDelete={() => setDeleteImageModal(true)}
            disabled={readOnly}
          />
        </Div>
        <Div $flex $column $gap="24px">
          <AdminInput
            placeholder="Enter here"
            label="Item Name"
            value={name}
            onChange={({ target }: { target: HTMLInputElement }) =>
              setName(target.value)
            }
            disabled={readOnly}
          />

          <AdminSelect
            value={category}
            options={typeOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              setCategory(Number(target.value));
            }}
            // placeholder="Select location"
            label="Choose Category"
            disabled={readOnly}
          />

          <Div $flex $alignCenter $gap="24px">
            <AdminDollarInput2
              label="Price"
              onValueChange={handleValueTotalChange}
              value={price}
            />
          </Div>

          <AdminSelect
            value={JSON.stringify({
              availability: availability,
              pausedUntilEndOfTheDay: pausedUntilEndOfTheDay,
            })}
            options={availabilityOptions}
            onChange={({ target }: { target: HTMLSelectElement }) => {
              setAvailability(
                JSON.parse(target.value)?.availability as Availability
              );
              setPausedUntilEndOfTheDay(
                JSON.parse(target.value)?.pausedUntilEndOfTheDay || false
              );
            }}
            label="Availability"
            disabled={readOnly}
          />

          <AdminInput
            placeholder="Enter here"
            label="Description"
            type="textarea"
            value={description}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setDescription(target.value)
            }
            disabled={readOnly}
          />

          <AdminInput
            placeholder="Enter here"
            label="Preparation Notes"
            type="textarea"
            value={preparationNotes}
            onChange={({ target }: { target: HTMLTextAreaElement }) =>
              setPreparationNotes(target.value)
            }
            disabled={readOnly}
          />

          {customizedPrices?.length > 0 ? (
            <div>
              <Div $flex $alignCenter $between>
                <H5 $mb="0">Custom prices for providers</H5>
                {!readOnly && (
                  <Icon $pointer onClick={() => setAddCustomPriceModal(true)}>
                    <PlusIcon />
                  </Icon>
                )}
              </Div>
              <div>
                <Div
                  $flex
                  $alignCenter
                  $between
                  // $mb="8"
                  $p="12px 24px"
                >
                  <Div $flex $alignCenter $gap="12px" $width="40%">
                    <P $left $m="0">
                      Provider
                    </P>
                  </Div>
                  <Div $width="20%">
                    <P $m="0">Price</P>
                  </Div>
                  <Div $width="20%"></Div>
                </Div>

                {customizedPrices?.map(
                  ({ provider, price, pickupPrice }, i) => (
                    <Div
                      $flex
                      $alignCenter
                      $between
                      key={i}
                      $mb="8"
                      $box
                      $p="16px 24px"
                      $pointer={!readOnly}
                      onClick={() => {
                        if (readOnly) return;
                        setEditCustomPriceModal({
                          provider,
                          price,
                          pickupPrice,
                        });
                      }}
                    >
                      <Div $flex $alignCenter $gap="12px" $width="40%">
                        <Img
                          alt=""
                          $width="32px"
                          src={renderProviderIcon(provider)}
                        />
                        <P $bold $m="0" $left>
                          {renderProviderName(provider)}
                        </P>
                      </Div>
                      <Div $width="20%">
                        <P $m="0">
                          {pickupPrice
                            ? displayCentsInDollars(pickupPrice)
                            : displayCentsInDollars(price)}
                        </P>
                      </Div>
                      <Div $width="20%" $flex $justifyEnd $gap="8px">
                        {!readOnly && (
                          <>
                            <Icon $size="32">
                              <EditPenIconRed />
                            </Icon>
                            <Icon
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteModal({
                                  provider,
                                  price,
                                  pickupPrice,
                                });
                              }}
                            >
                              <TrashIconRed />
                            </Icon>
                          </>
                        )}
                      </Div>
                    </Div>
                  )
                )}
              </div>
            </div>
          ) : (
            !readOnly && (
              <Div
                $flex
                $alignCenter
                $gap="8px"
                $pointer
                onClick={() => setAddCustomPriceModal(true)}
              >
                <Icon>
                  <PlusIcon />
                </Icon>

                <H5 $m="0" $color={lightTheme.primary}>
                  Add Custom Prices for Providers
                </H5>
              </Div>
            )
          )}

          {modifierGroups?.length > 0 ? (
            <>
              <Div $flex $alignCenter $between>
                <H5 $mb="0">Modifier Groups</H5>
                {!readOnly && (
                  <Icon $pointer onClick={() => setAddModifierGroupModal(true)}>
                    <PlusIcon />
                  </Icon>
                )}
              </Div>
              <div>
                {modifierGroups?.map((item, i) => (
                  <AddModifierGroupComponent
                    item={item}
                    key={i}
                    edit
                    modifierGroups={modifierGroups}
                    setModifierGroups={setModifierGroups}
                    readOnly={readOnly}
                  />
                ))}
              </div>
            </>
          ) : (
            !readOnly && (
              <Div
                $flex
                $alignCenter
                $gap="8px"
                $pointer
                onClick={() => setAddModifierGroupModal(true)}
              >
                <Icon>
                  <PlusIcon />
                </Icon>

                <H5 $m="0" $color={lightTheme.primary}>
                  Add Modifier Groups
                </H5>
              </Div>
            )
          )}
        </Div>
      </Modal>

      <AddCustomPrice
        customPriceModal={addCustomPriceModal}
        closeModal={() => {
          setAddCustomPriceModal(false);
        }}
        customizedPrices={customizedPrices}
        setCustomizedPrices={setCustomizedPrices}
      />

      <AddCustomPrice
        customPriceModal={editCustomPriceModal}
        closeModal={() => {
          setEditCustomPriceModal(null);
        }}
        customizedPrices={customizedPrices}
        setCustomizedPrices={setCustomizedPrices}
        edit
      />

      <DeleteCustomPriceModal
        deleteModal={deleteModal}
        closeModal={() => setDeleteModal(null)}
        setCustomizedPrices={setCustomizedPrices}
      />

      <DeleteImageModal
        deleteModal={deleteImageModal}
        closeModal={() => setDeleteImageModal(false)}
        setImage={setImage}
      />
    </>
  );
};
